/**
 * Role-token matching for document-template `appliesToRoles` filtering, used by the
 * `documents-cards` entity template (#20323 fase 2) to show only templates that apply
 * to the current user's roles.
 *
 * WHY tolerant matching: a document-template's `appliesToRoles` is authored by the
 * `custom-userroles-multicheckbox` field (`userroles.component.ts`), which stores role
 * **IDs (UUIDs)**. However a large body of existing/seed templates stores role **SLUGS**
 * ("vervoerder", "chauffeur", "onboarder"). Both formats are live at once, so rather than
 * force a broad config data-migration, we match tolerantly: a template applies to the
 * user when any configured role token equals the user's role id OR the slug/name of one
 * of the user's roles.
 *
 * Kept as pure, framework-free functions so the matching logic is unit-testable without
 * bootstrapping Angular.
 */

export interface RoleLike {
  id?: string | null;
  name?: string | null;
}

/**
 * Build the set of identifiers representing the current user's roles: each role's id
 * plus its name and the slug of its name. `slugify` is injected so callers reuse the
 * app's slug convention (`UtilityService.createSlug`) without this util depending on it.
 */
export function buildRoleTokenSet(
  roleIds: readonly string[],
  allRoles: readonly RoleLike[],
  slugify: (text: string) => string | null
): Set<string> {
  const tokens = new Set<string>();
  for (const id of roleIds ?? []) {
    if (!id) continue;
    tokens.add(id);
    const role = allRoles?.find(r => r.id === id);
    if (role?.name) {
      tokens.add(role.name);
      const slug = slugify(role.name);
      if (slug) tokens.add(slug);
    }
  }
  return tokens;
}

/**
 * A template with no/empty `appliesToRoles` applies to everyone; the `'all'` sentinel
 * applies to everyone; otherwise at least one configured token (a string, or the `id` of
 * an object entry) must be present in `roleTokens`.
 */
export function templateAppliesToRoleTokens(appliesToRoles: unknown, roleTokens: ReadonlySet<string>): boolean {
  const list = Array.isArray(appliesToRoles) ? appliesToRoles : [];
  if (list.length === 0) return true;
  const tokens = list
    .map(r => (typeof r === 'string' ? r : ((r as RoleLike | null)?.id ?? null)))
    .filter((t): t is string => !!t);
  if (tokens.length === 0) return true;
  return tokens.some(t => t === 'all' || roleTokens.has(t));
}
